import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Wallet, Check, X, ChevronRight, CalendarDays, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const currency = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * InspectorPreRoutePage
 * Angela Feb 2026 — PRD Inspector App / Item 4
 *
 * The inspector's "morning briefing" screen. Lists the orders assigned
 * to today, highlights the total expected payment, and requires the
 * inspector to accept or refuse the route before working the day.
 * Refusing requires a reason from a fixed list.
 */
const InspectorPreRoutePage = ({ Layout }) => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [refusing, setRefusing] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API}/inspector/pre-route`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load your route.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision, reason) => {
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/inspector/pre-route/decide`, {
        date: data?.date, decision, reason: reason || undefined,
      });
      setRefusing(false); setRefuseReason("");
      await load();
      if (decision === "accept") {
        // navigate to My Orders after accepting
        setTimeout(() => nav("/my-orders"), 500);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit decision.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24" data-testid="pre-route-loading">
          <Loader2 className="h-6 w-6 animate-spin text-[#2F5D8A]" />
        </div>
      </Layout>
    );
  }

  const decision = data?.decision?.decision;
  const isAccepted = decision === "accept";
  const isRefused = decision === "refuse";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4" data-testid="inspector-pre-route-page">
        {/* Header card — navy + gold */}
        <div className="rounded-2xl overflow-hidden shadow-lg border border-[#2F5D8A]/20">
          <div className="bg-gradient-to-br from-[#2F5D8A] to-[#1F3F60] text-white px-6 py-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#D1A054] font-bold">
              <CalendarDays className="h-3.5 w-3.5" /> Today’s route
            </div>
            <h1 className="text-lg font-bold mt-1" data-testid="pre-route-date">{data?.date}</h1>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">Expected payout</p>
                <p className="text-4xl font-black text-[#D1A054] mt-1" data-testid="pre-route-total">{currency(data?.total_expected)}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">Orders</p>
                <p className="text-2xl font-bold text-white mt-1" data-testid="pre-route-count">{data?.count || 0}</p>
              </div>
            </div>
          </div>

          {/* Decision area */}
          {!decision && data?.count > 0 && !refusing && (
            <div className="bg-white px-6 py-4 grid grid-cols-2 gap-3">
              <button
                onClick={() => decide("accept")}
                disabled={submitting}
                className="flex items-center justify-center gap-2 py-3 rounded-lg bg-[#D1A054] hover:bg-[#B98F42] text-white text-sm font-bold disabled:opacity-50"
                data-testid="pre-route-accept-btn"
              >
                <Check className="h-4 w-4" /> Accept route
              </button>
              <button
                onClick={() => setRefusing(true)}
                disabled={submitting}
                className="flex items-center justify-center gap-2 py-3 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 text-sm font-bold disabled:opacity-50"
                data-testid="pre-route-refuse-btn"
              >
                <X className="h-4 w-4" /> Refuse route
              </button>
            </div>
          )}

          {refusing && (
            <div className="bg-white px-6 py-4 space-y-3" data-testid="pre-route-refuse-panel">
              <p className="text-xs font-semibold text-gray-600">Select a reason:</p>
              <div className="space-y-2">
                {Object.entries(data?.refuse_reasons || {}).map(([code, label]) => (
                  <label key={code} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="refuse_reason"
                      value={code}
                      checked={refuseReason === code}
                      onChange={(e) => setRefuseReason(e.target.value)}
                      data-testid={`refuse-reason-${code}`}
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button onClick={() => { setRefusing(false); setRefuseReason(""); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                <button
                  onClick={() => decide("refuse", refuseReason)}
                  disabled={submitting || !refuseReason}
                  className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                  data-testid="pre-route-refuse-confirm"
                >
                  {submitting ? "Submitting…" : "Confirm refusal"}
                </button>
              </div>
            </div>
          )}

          {isAccepted && (
            <div className="bg-emerald-50 border-t border-emerald-200 px-6 py-3 flex items-center gap-2" data-testid="pre-route-accepted-banner">
              <Check className="h-4 w-4 text-emerald-700" />
              <p className="text-sm font-semibold text-emerald-800">You accepted this route. Good work!</p>
            </div>
          )}
          {isRefused && (
            <div className="bg-red-50 border-t border-red-200 px-6 py-3" data-testid="pre-route-refused-banner">
              <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
                <X className="h-4 w-4" /> You refused this route.
              </p>
              <p className="text-xs text-red-700 mt-0.5">Reason: {data?.refuse_reasons?.[data?.decision?.reason] || data?.decision?.reason}</p>
            </div>
          )}

          {data?.count === 0 && (
            <div className="bg-white px-6 py-6 text-center" data-testid="pre-route-empty">
              <p className="text-sm text-gray-500">No orders assigned for today.</p>
            </div>
          )}
          {error && <p className="text-xs text-red-600 px-6 py-2 bg-white" data-testid="pre-route-error">{error}</p>}
        </div>

        {/* Orders list */}
        {data?.count > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="pre-route-orders">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[#2F5D8A]" />
              <p className="text-xs font-bold text-[#1F2937] uppercase tracking-wider">Orders in this route</p>
            </div>
            <div className="divide-y divide-gray-100">
              {data.items.map((it, idx) => (
                <div key={it.id} className="px-5 py-3 flex items-start gap-3" data-testid={`pre-route-item-${it.order_number}`}>
                  <div className="w-7 h-7 rounded-full bg-[#2F5D8A]/10 text-[#2F5D8A] font-bold text-xs flex items-center justify-center flex-shrink-0">
                    {it.route_sequence ?? (idx + 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-[#1F2937] truncate">{it.order_number}</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2F5D8A]/10 text-[#2F5D8A] font-semibold">{it.order_type || "—"}</span>
                      {it.flow_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-semibold uppercase">{it.flow_type}</span>}
                    </div>
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5 truncate">
                      <MapPin className="h-3 w-3 flex-shrink-0" /> {it.address}{it.city ? `, ${it.city}` : ""}{it.state ? `, ${it.state}` : ""} {it.zip_code}
                    </p>
                    {(it.appointment_sg_date || it.appointment_sg_time) && (
                      <p className="text-[11px] text-gray-400 mt-0.5">Appt: {it.appointment_sg_date} {it.appointment_sg_time}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-[#D1A054]" data-testid={`pre-route-item-amount-${it.order_number}`}>{currency(it.expected_payment)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500 font-semibold">Total</p>
              <p className="text-sm font-bold text-[#2F5D8A]">{currency(data.total_expected)}</p>
            </div>
          </div>
        )}

        {isAccepted && (
          <button
            onClick={() => nav("/my-orders")}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-bold"
            data-testid="pre-route-go-to-orders"
          >
            Go to My Orders <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </Layout>
  );
};

export default InspectorPreRoutePage;
